/**
 * Sellox — Universal GET Response Cache
 *
 * Every successful GET is mirrored to IndexedDB by URL. On a future failed
 * GET (network down, server unreachable), we return the cached payload as
 * a synthetic 200 response so the page renders last-known data instead of
 * an empty skeleton.
 *
 * Combined with syncQueue.js, this delivers "use every feature offline":
 *   • READS  → served from this cache
 *   • WRITES → queued by syncQueue, replayed when online
 *
 * Excluded paths are listed in NO_CACHE — they hold non-cacheable
 * server-state (auth, AI streams, image proxies, etc.).
 */

import { scopedKey } from "./cacheScope";

const DB_NAME = "sellox_get_cache";
const STORE = "responses";
const DB_VERSION = 1;
const MAX_ENTRIES = 600;       // hard cap to keep IDB bounded
const TTL_MS = 30 * 24 * 60 * 60 * 1000;  // 30 days — long enough for 1+ month offline use

// Paths we never cache (changes too fast OR sensitive OR binary)
const NO_CACHE = [
  "/auth/",                        // tokens shouldn't persist
  "/payment-verify/inline/",       // images already cached separately
  "/payment-verify/image/",
  "/payment-verify/store/",        // realtime polling
  "/notifications/",               // realtime
  "/ws/",                          // websockets
  "/openapi.json",
  "/health",
  "/uploads/",
  "/credits-ai/stream",            // streaming endpoints
  "/ai/chat/stream",
];

let dbInstance = null;

function _open() {
  return new Promise((resolve, reject) => {
    if (dbInstance) return resolve(dbInstance);
    if (typeof indexedDB === "undefined") return reject(new Error("no indexedDB"));
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const os = db.createObjectStore(STORE, { keyPath: "url" });
        os.createIndex("cached_at", "cached_at");
      }
    };
    req.onsuccess = () => { dbInstance = req.result; resolve(req.result); };
    req.onerror = () => reject(req.error);
  });
}

function _key(config) {
  const { url, params } = config || {};
  let k = url || "";
  if (params && typeof params === "object") {
    const sorted = Object.keys(params).sort().map((p) => `${p}=${params[p]}`).join("&");
    if (sorted) k += `?${sorted}`;
  }
  return scopedKey(config, k);
}

function _isExcluded(url) {
  if (!url) return true;
  return NO_CACHE.some((p) => url.includes(p));
}

export async function cacheGetResponse(config, response) {
  try {
    if (!response || !config || (config.method || "get").toLowerCase() !== "get") return;
    if (_isExcluded(config.url)) return;
    if (response.status < 200 || response.status >= 300) return;
    // Skip if data is huge (>2MB) — likely a large export, don't bloat IDB
    let data = response.data;
    let size = 0;
    try {
      size = JSON.stringify(data).length;
    } catch {
      return; // not JSON-serializable
    }
    if (size > 2 * 1024 * 1024) return;

    const db = await _open();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put({
        url: _key(config),
        data,
        cached_at: Date.now(),
        size,
      });
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
  } catch (e) {
    // Cache failures are non-fatal
  }
}

export async function lookupCachedResponse(config) {
  try {
    if (!config) return null;
    if ((config.method || "get").toLowerCase() !== "get") return null;
    if (_isExcluded(config.url)) return null;
    const db = await _open();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).get(_key(config));
      req.onsuccess = () => {
        const v = req.result;
        if (!v) return resolve(null);
        if ((Date.now() - v.cached_at) > TTL_MS) return resolve(null);
        resolve(v);
      };
      req.onerror = () => resolve(null);
    });
  } catch { return null; }
}

/** All cached entries whose key starts with `prefix` (e.g. "/api/products") — used to rebuild a local search pool offline. */
export async function listCachedResponses(prefix, config) {
  try {
    prefix = scopedKey(config, prefix);
    const db = await _open();
    return new Promise((resolve) => {
      const out = [];
      const req = db.transaction(STORE, "readonly").objectStore(STORE).openCursor();
      req.onsuccess = (e) => {
        const c = e.target.result;
        if (!c) return resolve(out);
        const k = c.value.url || "";
        if ((k === prefix || k.startsWith(prefix + "?")) && (Date.now() - c.value.cached_at) <= TTL_MS) out.push(c.value);
        c.continue();
      };
      req.onerror = () => resolve(out);
    });
  } catch { return []; }
}

/** Periodic eviction (keep under MAX_ENTRIES) */
export async function trimCache() {
  try {
    const db = await _open();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE, "readwrite");
      const os = tx.objectStore(STORE);
      const req = os.getAll();
      req.onsuccess = () => {
        const all = req.result || [];
        if (all.length <= MAX_ENTRIES) return resolve();
        // Drop oldest 20% of entries
        const sorted = all.sort((a, b) => a.cached_at - b.cached_at);
        const toRemove = sorted.slice(0, Math.floor(all.length * 0.2));
        toRemove.forEach((e) => os.delete(e.url));
        resolve();
      };
      req.onerror = () => resolve();
    });
  } catch {}
}

// Trim once a day
if (typeof window !== "undefined") {
  setTimeout(trimCache, 30000);
  setInterval(trimCache, 24 * 60 * 60 * 1000);
}
